import axios from 'axios';
import {
  clearStoredTokens,
  getStoredAccessToken,
  getStoredRefreshToken,
  setStoredTokens,
} from './services/tokenStorage';
// No longer need Cookies for JWT authentication (unless you store tokens in HTTP-only cookies)
// import Cookies from 'js-cookie';

const api = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL?.replace(/\/+$/, '') + '/api/',
  // withCredentials is generally NOT needed for token-based auth
  // unless you explicitly decide to store tokens in HTTP-only cookies,
  // which reintroduces some complexity. For simple localStorage/sessionStorage, remove this.
  // withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 10_000, // abort if a request takes longer than 10s
});

// For JWT, you don't need these:
// api.defaults.xsrfCookieName = 'csrftoken';
// api.defaults.xsrfHeaderName = 'X-CSRFToken';

// Request interceptor: add Authorization header with access token for JWT
api.interceptors.request.use(
  async config => {
    const accessToken = getStoredAccessToken();
    if (accessToken) {
      config.headers.Authorization = `Bearer ${accessToken}`; // Set Authorization header
    }
    return config;
  },
  error => {
    return Promise.reject(error);
  }
);

// Singleton refresh promise — prevents concurrent 401s from each firing their
// own refresh call, which would race and invalidate each other's tokens.
let refreshPromise = null;

// Response interceptor: handle token expiration and refresh for JWT
api.interceptors.response.use(
  res => res,
  async error => {
    const originalRequest = error.config;

    // Network / non-HTTP error (e.g. Render cold-start timeout, offline) —
    // do NOT treat as an auth failure; propagate without clearing tokens.
    if (!error.response) {
      return Promise.reject(error);
    }

    const refreshUrl =
      process.env.REACT_APP_API_BASE_URL?.replace(/\/+$/, '') + '/api/token/refresh/';

    if (
      error.response.status === 401 &&
      !originalRequest._retry &&
      originalRequest.url !== refreshUrl
    ) {
      originalRequest._retry = true;
      const refreshToken = getStoredRefreshToken();

      if (!refreshToken) {
        clearStoredTokens();
        window.location.href = '/login';
        return Promise.reject(error);
      }

      // Deduplicate: all concurrent 401s share a single refresh call so
      // rotating refresh tokens aren't invalidated by parallel attempts.
      if (!refreshPromise) {
        refreshPromise = axios
          .post(refreshUrl, { refresh: refreshToken })
          .finally(() => { refreshPromise = null; });
      }

      try {
        const res = await refreshPromise;
        setStoredTokens({ access: res.data.access, refresh: res.data.refresh });
        originalRequest.headers.Authorization = `Bearer ${res.data.access}`;
        return api(originalRequest);
      } catch (refreshError) {
        // Only force logout on an actual HTTP auth failure from the refresh
        // endpoint — not on network errors such as a backend cold start.
        if (refreshError.response) {
          console.error('Refresh token failed. Logging out.', refreshError.response.data);
          clearStoredTokens();
          window.location.href = '/login';
        }
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

export default api;


